import React, { useState } from 'react';
import {
  Button,
  Box,
  Modal,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Typography,
  Dialog,
  Stack,
} from '@mui/material';

import StatisticsTable from './StatisticsTable';
import MuxIdDrilldown from './MuxIdDrilldown';
import CustomModal from '../../common/CustomModal';

import SystemMonitorFormattedMessages from '../../../messages/admin/SystemMonitorFormattedMessages';

import useAdminRequest from '../../../requests/AdminRequest';

const Statistics = (props) => {
  const [selectedMuxID, setSelectedMuxID] = useState(null);
  const [isTwilio, setIsTwilio] = useState(false);
  const [muxRestarting, setMuxRestarting] = useState(0);
  const [apiRestarting, setApiRestarting] = useState(0);
  const [showMuxRestartConfirmation, setShowMuxRestartConfirmation] =
    useState(false);
  const [showApiRestartConfirmation, setShowApiRestartConfirmation] =
    useState(false);
  const { sendMuxShutdown, sendApiShutdown } = useAdminRequest();

  const muxIdDrilldown = (e, isTwilio) => {
    setSelectedMuxID(e);
    setIsTwilio(isTwilio);
  };

  const backToStatsPage = () => {
    setSelectedMuxID(null);
  };

  const handleMuxModal = () => {
    setShowMuxRestartConfirmation(!showMuxRestartConfirmation);
    setMuxRestarting(0);
  };

  const restartMux = async () => {
    setMuxRestarting(1);
    let result = await sendMuxShutdown(props.errorHandler);
    if (result.length === 0) {
      setMuxRestarting(3);
    } else {
      setMuxRestarting(2);
    }
  };

  const handleApiModal = () => {
    setShowApiRestartConfirmation(!showApiRestartConfirmation);
    setApiRestarting(0);
  };

  const restartApi = async () => {
    setApiRestarting(1);
    let result = await sendApiShutdown(props.errorHandler);
    if (result.length === 0) {
      setApiRestarting(3);
    } else {
      setApiRestarting(2);
    }
  };

  const showMuxConfirmationDialog = () => {
    if (muxRestarting <= 1) {
      return (
        <Dialog open={showMuxRestartConfirmation} onClose={handleMuxModal}>
          <DialogTitle variant="h2">
            {SystemMonitorFormattedMessages.restartMuxModalTitle}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {muxRestarting === 0
                ? SystemMonitorFormattedMessages.restartMuxModalBody
                : 'Mux Restarting...'}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              disabled={muxRestarting === 0 ? false : true}
              variant="outlined"
              color="secondary"
              onClick={handleMuxModal}
            >
              No
            </Button>
            <Button
              disabled={muxRestarting === 0 ? false : true}
              variant="contained"
              color="secondary"
              onClick={restartMux}
            >
              Yes
            </Button>
          </DialogActions>
        </Dialog>
      );
    } else {
      return (
        <Dialog open={showMuxRestartConfirmation} onClose={handleMuxModal}>
          <DialogTitle variant="h2">
            {SystemMonitorFormattedMessages.restartMuxModalTitle}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {muxRestarting === 2
                ? 'Mux Restart Succeeded'
                : 'Mux Restart Failed'}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleMuxModal}
            >
              Ok
            </Button>
          </DialogActions>
        </Dialog>
      );
    }
  };

  const showApiConfirmationDialog = () => {
    if (apiRestarting <= 1) {
      return (
        <Dialog open={showApiRestartConfirmation} onClose={handleApiModal}>
          <DialogTitle variant="h2">
            {SystemMonitorFormattedMessages.restartApiModalTitle}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {apiRestarting === 0
                ? SystemMonitorFormattedMessages.restartApiModalBody
                : 'Api Restarting...'}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              disabled={apiRestarting === 0 ? false : true}
              variant="outlined"
              color="secondary"
              onClick={handleApiModal}
            >
              No
            </Button>
            <Button
              disabled={apiRestarting === 0 ? false : true}
              variant="contained"
              color="secondary"
              onClick={restartApi}
            >
              Yes
            </Button>
          </DialogActions>
        </Dialog>
      );
    } else {
      return (
        <Dialog open={showApiRestartConfirmation} onClose={handleApiModal}>
          <DialogTitle variant="h2">
            {SystemMonitorFormattedMessages.restartApiModalTitle}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {apiRestarting === 2
                ? 'Api Restart Succeeded'
                : 'Api Restart Failed'}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleApiModal}
            >
              Ok
            </Button>
          </DialogActions>
        </Dialog>
      );
    }
  };

  const generateStatsPage = () => {
    if (selectedMuxID) {
      return (
        <MuxIdDrilldown
          selectedMuxID={selectedMuxID}
          isTwilio={isTwilio}
          backToStats={backToStatsPage}
          getMuxIdDrilldown={props.getMuxIdDrilldown}
        />
      );
    } else {
      return (
        <>
          <StatisticsTable
            primaryQueueStats={props.primaryQueueStats}
            totalStats={props.totalStats}
            totalTwilioStats={props.totalTwilioStats}
            statsPageReady={props.statsPageReady}
            muxIdDrilldown={muxIdDrilldown}
          />

          <Typography variant="h3" gutterBottom>
            {SystemMonitorFormattedMessages.restartButtonTitle}
          </Typography>

          <Stack sx={{ flexDirection: { md: 'row' }, gap: 2 }}>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleApiModal}
            >
              {SystemMonitorFormattedMessages.restartApiButton}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleMuxModal}
            >
              {SystemMonitorFormattedMessages.restartMuxButton}
            </Button>
          </Stack>
          {showMuxConfirmationDialog()}
          {showApiConfirmationDialog()}
        </>
      );
    }
  };

  return generateStatsPage();
};

export default Statistics;
